import React, { useMemo } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { getCountryCode } from '@/modules/common/store/selectors';
import { PayoutsForm } from '@/modules/onboarding/model/payoutsStep';
import { RUPartnerType } from '@/modules/partner/model/partnerType';
import { CountryCode } from '@/shared/models/country-code';
import { BottomButtons } from '@/shared/view/BottomButtons';
import { Section } from '@/shared/view/Section';
import { Button } from '@src-common/view/adapters/Button';
import {
  DocsList,
  IDocsListItem,
  HandleDocListItemClick,
} from '@src-common/view/components/DocsList';

import { FrPaymentType } from './LocalizedForm/fr';
import { GbPaymentType } from './LocalizedForm/gb';
import { RuPaymentType } from './LocalizedForm/ru';
import { UsPaymentType } from './LocalizedForm/us';
import s from './styles.module.css';

type Props = {
  control: Control<PayoutsForm>;
  docs: IDocsListItem[];
  onDocListItemClick: HandleDocListItemClick;
  isSubmitting?: boolean;
};

const PaymentTypeView: React.FC<Props> = ({ isSubmitting, docs, onDocListItemClick, control }) => {
  const intl = useIntl();

  const countryCode = useSelector(getCountryCode);

  const form = useMemo(() => {
    switch (countryCode) {
      case CountryCode.Russia: {
        return <RuPaymentType control={control} />;
      }
      case CountryCode.France: {
        return <FrPaymentType control={control} />;
      }
      case CountryCode.UnitedStates: {
        return <UsPaymentType control={control} />;
      }
      default:
        return <GbPaymentType control={control} />;
    }
  }, [countryCode, control]);

  const selectedPartnerType = useWatch({ control, name: 'partnerType' });

  return (
    <>
      <Section>
        {form}
        {docs.length > 0 && (
          <DocsList
            title={intl.formatMessage({
              defaultMessage: 'ADD DOCUMENTS',
              id: 'pytlFK',
              description: 'Docs list title',
            })}
            className={s.docs}
            onChange={onDocListItemClick}
            docs={docs}
            isStatusHidden={true}
          />
        )}
      </Section>
      <BottomButtons>
        <Button
          type="submit"
          isPending={isSubmitting}
          isDisabled={
            selectedPartnerType === RUPartnerType.selfEmployed && countryCode === CountryCode.Russia
          }
        >
          <FormattedMessage
            defaultMessage="NEXT"
            id="6jg2Fp"
            description="OnBoarding next button text"
          />
        </Button>
      </BottomButtons>
    </>
  );
};

export { PaymentTypeView };
